// src/pages/Login/Login.tsx
import React from 'react';
import api from 'services/api/config/api';
import { clearScopedTenantId } from 'services/api/config/scopedTenant';
import { GoogleOAuthProvider, CredentialResponse } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { ForgotPasswordModal } from 'components';
import { assetPath } from 'utils/getBasePath';

import { LoginCredentialsForm, LoginFooter, NewPasswordChallengeForm } from './components';
import { useLoginFlow } from './hooks/useLoginFlow';
import styles from './Login.module.scss';

const Login = () => {
    const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
    const {
        error,
        setError,
        isLoading,
        setIsLoading,
        isForgotPasswordModalOpen,
        setIsForgotPasswordModalOpen,
        needsNewPassword,
        passwordChallengeDone,
        newPassword,
        setNewPassword,
        confirmPassword,
        setConfirmPassword,
        challengeEmail,
        navigate,
        handleLogin,
        handleNewPasswordSubmit,
        handleBackToLogin,
    } = useLoginFlow();

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
        setIsLoading(true);
        setError(null);

        try {
            clearScopedTenantId();
            const decodedToken = jwtDecode<{ email: string; name: string; sub: string }>(
                credentialResponse.credential || ''
            );
            const { email, name, sub } = decodedToken;

            const response = await api.post('/auth/google-login', {
                googleToken: credentialResponse.credential,
                email,
                name,
                googleId: sub,
            });

            const { userRole } = response.data;

            localStorage.setItem('userRole', userRole || '');

            navigate('/dashboard');
        } catch (err) {
            console.error('Google login error:', err);
            setError(
                (err as { response?: { data?: { error: string } }; message: string })?.response?.data?.error ||
                    (err as { message: string })?.message ||
                    'Google login failed'
            );
        } finally {
            setIsLoading(false);
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleGoogleError = () => {
        setError('Google login failed. Please try again.');
        setIsLoading(false);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleDemoClick = () => {
        navigate('/demo-request');
    };

    const logoUrl = assetPath('/SLAILOGO.png');

    return (
        <GoogleOAuthProvider clientId={googleClientId}>
            <div className={styles.loginPageContainer}>
                <div className={styles.loginBox}>
                    <div className={styles.loginLogoRow}>
                        <img src={logoUrl} alt='SLAI Logo' className={styles.slaiLogo} />
                        <h2 className={styles.slaiText}>straightline.ai</h2>
                    </div>
                    <p className={styles.demoText}>
                        {needsNewPassword
                            ? passwordChallengeDone
                                ? 'Finishing sign-in…'
                                : 'Choose a new password to finish signing in.'
                            : 'Need access? Contact your administrator for access.'}
                    </p>

                    {!needsNewPassword ? (
                        <LoginCredentialsForm
                            isLoading={isLoading}
                            error={error}
                            onSubmit={handleLogin}
                            onForgotPassword={() => setIsForgotPasswordModalOpen(true)}
                        />
                    ) : (
                        <NewPasswordChallengeForm
                            email={challengeEmail}
                            passwordChallengeDone={passwordChallengeDone}
                            newPassword={newPassword}
                            confirmPassword={confirmPassword}
                            error={error}
                            isLoading={isLoading}
                            onNewPasswordChange={setNewPassword}
                            onConfirmPasswordChange={setConfirmPassword}
                            onSubmit={handleNewPasswordSubmit}
                            onBack={handleBackToLogin}
                        />
                    )}

                    <LoginFooter onPrivacyClick={() => navigate('/privacy')} onTermsClick={() => navigate('/terms')} />
                </div>
            </div>

            <ForgotPasswordModal
                isOpen={isForgotPasswordModalOpen}
                onClose={() => setIsForgotPasswordModalOpen(false)}
            />
        </GoogleOAuthProvider>
    );
};

export default Login;
